/**
 * Hook for managing player sidebar state
 *
 * Provides access to the currently selected player in the sidebar
 * and a function to update the selection.
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback } from "react";

import { selectedPlayerIdVar } from "../stores/kanbanReactiveVars";

/**
 * Return type for usePlayerSidebar hook
 */
interface UsePlayerSidebarResult {
  /**
   * The ID of the currently selected player, or null if no player is selected
   */
  selectedPlayerId: string | null;

  /**
   * Callback fired when updating the selected player
   * @param playerId - The player ID to select, or null to clear selection
   */
  setSelectedPlayer: (playerId: string | null) => void;
}

/**
 * Hook for accessing and updating the selected player in the sidebar
 * @returns Object containing selectedPlayerId and setSelectedPlayer function
 * @example
 * ```typescript
 * const { selectedPlayerId, setSelectedPlayer } = usePlayerSidebar();
 *
 * // Select a player
 * setSelectedPlayer("player-123");
 *
 * // Clear selection
 * setSelectedPlayer(null);
 * ```
 */
export function usePlayerSidebar(): UsePlayerSidebarResult {
  const selectedPlayerId = useReactiveVar(selectedPlayerIdVar);

  const setSelectedPlayer = useCallback((playerId: string | null) => {
    selectedPlayerIdVar(playerId);
  }, []);

  return { selectedPlayerId, setSelectedPlayer };
}
